/**
 * **Train** — one case for QueVee Voice from the chart on screen
 * (`docs/plans/quevee-voice.md` §6; the text mock approved 2026-10-18).
 *
 * Everything is taken off the screen when the dialog opens — the picture, the
 * chart's snapshot, the whole screener row, the last bar — so what is saved is
 * what was looked at, even if the chart moves while the read is spoken. The
 * server adds the symbol's profile and fundamentals and freezes the lot.
 *
 * **The read is spoken into our own Whisper**, never the browser's speech
 * service: John's words are SweeterTrade's. Hold the key, talk, let go; the
 * text is appended to the box, where it can be edited before Save. Each hold
 * is kept as a recording of the case.
 *
 * A platform admin's test for now, saved as **Sample** by default: the test
 * bin, never trained on.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { Frequency } from "@qufolio/model";

import { train, transcribe, type Condition, type Voice, type VoiceFile } from "../api/voice";
import { chartImage, chartSnapshot } from "../lib/chart-snapshot";
import { chartTable } from "../lib/chart-export";
import { intervalLabel } from "../lib/interval-label";
import { screenerCommands } from "../lib/screener-commands";
import {
  appendRead,
  capturedLines,
  clock,
  lastBarDate,
  trainBody,
  type Captured,
  type TrainForm,
} from "../lib/train";
import { DraggableDialog } from "./DraggableDialog";

/** The store's file limit is 8 MB; a hi-DPI capture is shrunk well under it. */
const MAX_W = 2000;
const MAX_H = 1200;
const CONDITIONS: Condition[] = ["Bullish", "Neutral", "Bearish"];

/** A data URL shrunk to fit, as bare base64 — `CommunityComposer`'s rule. */
async function fitPng(dataUrl: string): Promise<string> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("the chart picture could not be read"));
    img.src = dataUrl;
  });
  const scale = Math.min(1, MAX_W / img.naturalWidth, MAX_H / img.naturalHeight);
  if (scale >= 1) return dataUrl.split(",", 2)[1] ?? "";
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.floor(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl.split(",", 2)[1] ?? "";
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png").split(",", 2)[1] ?? "";
}

function blobBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
    reader.onerror = () => reject(new Error("the recording could not be read"));
    reader.readAsDataURL(blob);
  });
}

async function capture(chartGroupId: string, symbol: string, frequency: Frequency): Promise<Captured> {
  const [snapshot, picture] = await Promise.all([chartSnapshot(chartGroupId), chartImage(chartGroupId)]);
  let image: string | undefined;
  if (picture) {
    try {
      image = await fitPng(picture);
    } catch {
      image = undefined;
    }
  }
  return {
    symbol,
    interval: frequency,
    barDate: lastBarDate(chartTable(chartGroupId)),
    chart: snapshot,
    screener: screenerCommands()?.rowOf?.(symbol),
    image,
  };
}

/** The microphone, held for one press: start on down, stop on up. */
function useSpeakKey(onClip: (clip: VoiceFile) => void) {
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const started = useRef(0);
  const [seconds, setSeconds] = useState(0);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!live) return;
    const tick = window.setInterval(() => setSeconds((Date.now() - started.current) / 1000), 250);
    return () => window.clearInterval(tick);
  }, [live]);

  // The microphone is released when the dialog closes, so the browser's
  // recording light goes out with it.
  useEffect(() => () => stream.current?.getTracks().forEach((t) => t.stop()), []);

  const start = useCallback(async () => {
    if (recorder.current) return;
    setError(null);
    try {
      stream.current ??= await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("The microphone is blocked — allow it for this site, or type your read.");
      return;
    }
    const type = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
    const rec = new MediaRecorder(stream.current, type ? { mimeType: type } : undefined);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = async () => {
      const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
      if (blob.size) onClip({ contentType: blob.type, data: await blobBase64(blob) });
    };
    recorder.current = rec;
    started.current = Date.now();
    setSeconds(0);
    setLive(true);
    rec.start();
  }, [onClip]);

  const stop = useCallback(() => {
    const rec = recorder.current;
    recorder.current = null;
    setLive(false);
    if (rec && rec.state !== "inactive") rec.stop();
  }, []);

  return { start, stop, live, seconds, error };
}

export function TrainDialog({
  chartGroupId,
  symbol,
  frequency,
  voices,
  onClose,
}: {
  chartGroupId: string;
  symbol: string;
  frequency: Frequency;
  voices: Voice[];
  onClose: () => void;
}) {
  const [captured, setCaptured] = useState<Captured | null>(null);
  const [form, setForm] = useState<TrainForm>({
    read: "",
    pattern: "",
    stop: "",
    target: "",
    origin: "sample",
  });
  const [voice, setVoice] = useState(voices[0]?.key ?? "");
  const [clips, setClips] = useState<VoiceFile[]>([]);
  const [hearing, setHearing] = useState(0);
  const [status, setStatus] = useState<{ tone: "ok" | "bad" | "info"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    void capture(chartGroupId, symbol, frequency).then((c) => live && setCaptured(c));
    return () => {
      live = false;
    };
  }, [chartGroupId, symbol, frequency]);

  const onClip = useCallback(
    (clip: VoiceFile) => {
      setClips((was) => [...was, clip]);
      setHearing((n) => n + 1);
      transcribe(voice, clip, symbol)
        .then(({ text }) => {
          setForm((f) => ({ ...f, read: appendRead(f.read, text) }));
          setStatus(null);
        })
        .catch((e: unknown) =>
          setStatus({ tone: "bad", text: `Voice to text: ${e instanceof Error ? e.message : "failed"}. The recording is kept; type the read.` }),
        )
        .finally(() => setHearing((n) => n - 1));
    },
    [voice, symbol],
  );
  const speak = useSpeakKey(onClip);

  const save = async () => {
    if (!captured || !voice) return;
    const out = trainBody(captured, form, clips);
    if ("error" in out) {
      setStatus({ tone: "bad", text: out.error });
      return;
    }
    setSaving(true);
    setStatus({ tone: "info", text: "Saving…" });
    try {
      const made = await train(voice, out.body);
      const missing = made.inputs?.missing ?? [];
      setSaved(true);
      setStatus({
        tone: "ok",
        text: `Saved as ${made.origin === "sample" ? "a sample" : "a post"}.${missing.length ? ` Not captured: ${missing.join(", ")}.` : ""}`,
      });
    } catch (e) {
      setStatus({ tone: "bad", text: e instanceof Error ? e.message : "The save failed." });
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof TrainForm>(key: K, value: TrainForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const title = `Train · ${symbol} · ${intervalLabel(frequency)}${captured?.barDate ? ` · bar ${captured.barDate}` : ""}`;

  return (
    <DraggableDialog title={title} onClose={onClose}>
      <div style={BODY}>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={THUMB}>
            {captured?.image ? (
              <img src={`data:image/png;base64,${captured.image}`} alt={`${symbol} chart`} style={{ width: "100%", display: "block" }} />
            ) : (
              <span style={MUTED}>{captured ? "no picture" : "Capturing…"}</span>
            )}
          </div>
          <div style={{ fontSize: 12, minWidth: 0 }}>
            <div style={{ ...LABEL, marginBottom: 4 }}>Captured</div>
            {captured &&
              capturedLines(captured).map((line) => (
                <div key={line.label} style={{ whiteSpace: "nowrap" }}>
                  <span style={{ color: line.ok ? "var(--qf-positive)" : "var(--qf-text-muted)" }}>{line.ok ? "✓" : "–"}</span>{" "}
                  {line.label} <span style={MUTED}>{line.detail}</span>
                </div>
              ))}
            <div style={{ ...MUTED, marginTop: 4 }}>+ symbol info and fundamentals, read at Save</div>
          </div>
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={LABEL}>Your read</span>
            <button
              type="button"
              aria-label="Hold to speak"
              aria-pressed={speak.live}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                void speak.start();
              }}
              onPointerUp={speak.stop}
              onPointerCancel={speak.stop}
              onKeyDown={(e) => {
                if ((e.key === " " || e.key === "Enter") && !e.repeat) {
                  e.preventDefault();
                  void speak.start();
                }
              }}
              onKeyUp={(e) => (e.key === " " || e.key === "Enter") && speak.stop()}
              disabled={saved}
              style={{ ...BUTTON, ...(speak.live ? { background: "var(--qf-negative)", color: "var(--qf-background)" } : {}) }}
            >
              ● {speak.live ? "Listening — let go to finish" : "Hold to speak"}
            </button>
            <span style={MUTED}>
              {speak.live ? clock(speak.seconds) : hearing > 0 ? "Writing it down…" : clips.length ? `${clips.length} recorded` : "0:00"}
            </span>
          </div>
          <textarea
            value={form.read}
            onChange={(e) => set("read", e.target.value)}
            placeholder="Your words appear here, and can be edited."
            rows={5}
            disabled={saved}
            style={{ ...FIELD, width: "100%", resize: "vertical", boxSizing: "border-box" }}
          />
          {speak.error && <div style={{ color: "var(--qf-negative)", fontSize: 12 }}>{speak.error}</div>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
          <span style={LABEL}>Condition</span>
          {CONDITIONS.map((c) => (
            <label key={c} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
              <input type="radio" name="train-condition" checked={form.condition === c} onChange={() => set("condition", c)} disabled={saved} />
              {c}
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, fontSize: 12, alignItems: "center" }}>
          <label style={INLINE}>
            Pattern
            <input value={form.pattern} onChange={(e) => set("pattern", e.target.value)} disabled={saved} style={{ ...FIELD, width: 150 }} />
          </label>
          <label style={INLINE}>
            Stop
            <input value={form.stop} onChange={(e) => set("stop", e.target.value)} inputMode="decimal" disabled={saved} style={{ ...FIELD, width: 70 }} />
          </label>
          <label style={INLINE}>
            Target
            <input value={form.target} onChange={(e) => set("target", e.target.value)} inputMode="decimal" disabled={saved} style={{ ...FIELD, width: 70 }} />
          </label>
        </div>

        <div style={{ display: "flex", gap: 12, fontSize: 12, alignItems: "center" }}>
          <label style={INLINE}>
            Voice
            <select value={voice} onChange={(e) => setVoice(e.target.value)} disabled={saved} style={FIELD}>
              {voices.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          <label style={INLINE}>
            Save as
            <select value={form.origin} onChange={(e) => set("origin", e.target.value as TrainForm["origin"])} disabled={saved} style={FIELD}>
              <option value="sample">Sample (test)</option>
              <option value="post">Post</option>
            </select>
          </label>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            role="status"
            style={{
              flex: 1,
              fontSize: 12,
              color:
                status?.tone === "bad" ? "var(--qf-negative)" : status?.tone === "ok" ? "var(--qf-positive)" : "var(--qf-text-secondary)",
            }}
          >
            {status?.text}
          </span>
          <button type="button" onClick={onClose} style={BUTTON}>
            {saved ? "Close" : "Cancel"}
          </button>
          {!saved && (
            <button
              type="button"
              onClick={() => void save()}
              disabled={!captured || saving || speak.live || hearing > 0}
              style={{ ...BUTTON, background: "var(--qf-accent)", color: "var(--qf-background)", borderColor: "var(--qf-accent)" }}
            >
              Save
            </button>
          )}
        </div>
      </div>
    </DraggableDialog>
  );
}

const BODY: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 12,
  width: "min(560px, calc(100vw - 32px))",
  boxSizing: "border-box",
  overflowY: "auto",
  color: "var(--qf-text)",
};
const THUMB: React.CSSProperties = {
  flex: "0 0 200px",
  height: 120,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-background)",
};
const LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 600 };
const MUTED: React.CSSProperties = { fontSize: 12, color: "var(--qf-text-muted)" };
const INLINE: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4 };
const FIELD: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "3px 6px",
  color: "var(--qf-text)",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
};
const BUTTON: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "4px 10px",
  color: "var(--qf-text)",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  cursor: "pointer",
};
